import React from "react";
import { Card, Form, Input, Select, Switch, Button, Space, Row, Col, Tag, Tooltip, InputNumber } from "antd";
import { PlusOutlined, DeleteOutlined, CopyOutlined, HolderOutlined } from "@ant-design/icons";
import type { AIActionButton } from "../types/aiTools";
import { ACTION_TYPES } from "../types/aiTools";

interface ActionButtonsEditorProps {
  value?: AIActionButton[];
  onChange?: (value: AIActionButton[]) => void;
}

const generateButtonId = () => `btn_${Date.now().toString(36)}`;

const createEmptyButton = (order: number): AIActionButton => ({
  id: generateButtonId(),
  label: "",
  actionType: "export",
  description: "",
  requiresUserInput: false,
  isDynamic: false,
  order,
});

export const ActionButtonsEditor: React.FC<ActionButtonsEditorProps> = ({
  value = [],
  onChange,
}) => {
  const addButton = () => {
    const newButton = createEmptyButton(value.length + 1);
    onChange?.([...value, newButton]);
  };

  const removeButton = (index: number) => {
    const newButtons = [...value];
    newButtons.splice(index, 1);
    newButtons.forEach((b, i) => (b.order = i + 1));
    onChange?.(newButtons);
  };

  const duplicateButton = (index: number) => {
    const btnToCopy = value[index];
    const newButton: AIActionButton = {
      ...btnToCopy,
      id: generateButtonId(),
      label: `${btnToCopy.label} (kopia)`,
      order: value.length + 1,
    };
    onChange?.([...value, newButton]);
  };

  const updateButton = (index: number, updates: Partial<AIActionButton>) => {
    const newButtons = [...value];
    newButtons[index] = { ...newButtons[index], ...updates };
    onChange?.(newButtons);
  };

  const moveButton = (fromIndex: number, direction: "up" | "down") => {
    const toIndex = direction === "up" ? fromIndex - 1 : fromIndex + 1;
    if (toIndex < 0 || toIndex >= value.length) return;

    const newButtons = [...value];
    [newButtons[fromIndex], newButtons[toIndex]] = [newButtons[toIndex], newButtons[fromIndex]];
    newButtons.forEach((b, i) => (b.order = i + 1));
    onChange?.(newButtons);
  };

  return (
    <div>
      <Space orientation="vertical" style={{ width: "100%" }} size="small">
        {value
          .sort((a, b) => (a.order || 0) - (b.order || 0))
          .map((btn, index) => (
            <Card
              key={btn.id}
              size="small"
              title={
                <Space>
                  <HolderOutlined style={{ cursor: "move", color: "#999" }} />
                  <Tag color={btn.isDynamic ? "blue" : "default"}>
                    {btn.isDynamic ? "Dynamiczny" : "Statyczny"}
                  </Tag>
                  <Tag color={btn.requiresUserInput ? "orange" : "green"}>
                    {btn.requiresUserInput ? "Wymaga input" : "Auto"}
                  </Tag>
                  <strong>{btn.label || btn.id}</strong>
                </Space>
              }
              extra={
                <Space size="small">
                  <Tooltip title="Duplikuj">
                    <Button
                      type="text"
                      size="small"
                      icon={<CopyOutlined />}
                      onClick={() => duplicateButton(index)}
                    />
                  </Tooltip>
                  <Tooltip title="Przesuń w górę">
                    <Button
                      type="text"
                      size="small"
                      icon="↑"
                      disabled={index === 0}
                      onClick={() => moveButton(index, "up")}
                    />
                  </Tooltip>
                  <Tooltip title="Przesuń w dół">
                    <Button
                      type="text"
                      size="small"
                      icon="↓"
                      disabled={index === value.length - 1}
                      onClick={() => moveButton(index, "down")}
                    />
                  </Tooltip>
                  <Tooltip title="Usuń">
                    <Button
                      type="text"
                      size="small"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => removeButton(index)}
                    />
                  </Tooltip>
                </Space>
              }
            >
              <Row gutter={16}>
                <Col span={6}>
                  <Form.Item label="ID" style={{ marginBottom: 8 }}>
                    <Input
                      value={btn.id}
                      onChange={(e) => updateButton(index, { id: e.target.value })}
                      placeholder="btn_xxx"
                    />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item label="Label" style={{ marginBottom: 8 }}>
                    <Input
                      value={btn.label}
                      onChange={(e) => updateButton(index, { label: e.target.value })}
                      placeholder="Nazwa przycisku"
                    />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item label="Typ akcji" style={{ marginBottom: 8 }}>
                    <Select
                      value={btn.actionType}
                      onChange={(v) => updateButton(index, { actionType: v })}
                      options={[...ACTION_TYPES]}
                    />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item label="Kolejność" style={{ marginBottom: 8 }}>
                    <InputNumber
                      value={btn.order}
                      onChange={(v) => updateButton(index, { order: v || 0 })}
                      min={1}
                      style={{ width: "100%" }}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={24}>
                  <Form.Item label="Opis" style={{ marginBottom: 8 }}>
                    <Input
                      value={btn.description}
                      onChange={(e) => updateButton(index, { description: e.target.value })}
                      placeholder="Krótki opis akcji..."
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={4}>
                  <Form.Item label="Wymaga input" style={{ marginBottom: 8 }}>
                    <Switch
                      checked={btn.requiresUserInput}
                      onChange={(v) => updateButton(index, { requiresUserInput: v })}
                    />
                  </Form.Item>
                </Col>
                <Col span={4}>
                  <Form.Item label="Dynamiczny" style={{ marginBottom: 8 }}>
                    <Switch
                      checked={btn.isDynamic}
                      onChange={(v) => updateButton(index, { isDynamic: v })}
                    />
                  </Form.Item>
                </Col>
              </Row>

              {btn.requiresUserInput && (
                <Row gutter={16}>
                  <Col span={8}>
                    <Form.Item label="Label input" style={{ marginBottom: 8 }}>
                      <Input
                        value={btn.userInputLabel}
                        onChange={(e) => updateButton(index, { userInputLabel: e.target.value })}
                        placeholder="np. Wprowadź tekst..."
                      />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item label="Placeholder" style={{ marginBottom: 8 }}>
                      <Input
                        value={btn.userInputPlaceholder}
                        onChange={(e) => updateButton(index, { userInputPlaceholder: e.target.value })}
                        placeholder="np. Tekst modyfikacji..."
                      />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item label="Opis input" style={{ marginBottom: 8 }}>
                      <Input
                        value={btn.userInputDescription}
                        onChange={(e) => updateButton(index, { userInputDescription: e.target.value })}
                        placeholder="np. Opisz zmiany..."
                      />
                    </Form.Item>
                  </Col>
                </Row>
              )}

              <div
                style={{
                  padding: 12,
                  backgroundColor: "#fafafa",
                  borderRadius: 8,
                  border: "1px solid #d9d9d9",
                  marginTop: 8,
                }}
              >
                <strong>Podgląd przycisku:</strong>
                <div style={{ marginTop: 8 }}>
                  <Button
                    type={btn.actionType === "export" || btn.actionType === "download" ? "primary" : "default"}
                  >
                    {btn.label || "Przycisk"}
                  </Button>
                  {btn.requiresUserInput && (
                    <Tag color="orange" style={{ marginLeft: 8 }}>
                      Wymaga input użytkownika
                    </Tag>
                  )}
                  {btn.isDynamic && (
                    <Tag color="blue" style={{ marginLeft: 4 }}>
                      Dynamiczny
                    </Tag>
                  )}
                </div>
              </div>
            </Card>
          ))}

        <Button type="dashed" onClick={addButton} icon={<PlusOutlined />} block>
          Dodaj przycisk akcji
        </Button>
      </Space>
    </div>
  );
};

export default ActionButtonsEditor;
